import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { ChevronDown, ChevronUp } from "lucide-react";
import { acceptAnalytics, declineAnalytics, getAnalyticsConsent } from "@/lib/analytics";

interface CategoryRowProps {
  title: string;
  description: string;
  locked?: boolean;
  checked: boolean;
  onCheckedChange?: (v: boolean) => void;
}

function CategoryRow({ title, description, locked, checked, onCheckedChange }: CategoryRowProps) {
  const [open, setOpen] = useState(false);

  return (
    <div className="py-3 border-b border-border last:border-0">
      <div className="flex items-center gap-3">
        <button
          type="button"
          className="flex flex-1 items-center gap-2 text-left min-w-0"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
        >
          {open ? (
            <ChevronUp className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          )}
          <span className="text-sm font-medium text-foreground">{title}</span>
        </button>

        {locked ? (
          <span className="text-xs font-medium text-muted-foreground shrink-0">Always on</span>
        ) : (
          <Switch
            checked={checked}
            onCheckedChange={onCheckedChange}
            aria-label={`${title} cookies`}
          />
        )}
      </div>

      {open && (
        <p className="mt-2 ml-5 text-xs text-muted-foreground leading-relaxed">{description}</p>
      )}
    </div>
  );
}

interface CookieBannerProps {
  onDismiss: () => void;
}

export function CookieBanner({ onDismiss }: CookieBannerProps) {
  const [analytics, setAnalytics] = useState(() => getAnalyticsConsent() !== "no");

  useEffect(() => {
    const main = document.getElementById("main-content");
    if (!main) return;
    const prev = main.style.paddingBottom;
    main.style.paddingBottom = "160px";
    return () => { main.style.paddingBottom = prev; };
  }, []);

  const handleSave = () => {
    if (analytics) {
      acceptAnalytics();
    } else {
      declineAnalytics();
    }
    onDismiss();
  };

  const handleAcceptAll = () => {
    setAnalytics(true);
    acceptAnalytics();
    onDismiss();
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-background shadow-[0_-4px_24px_rgba(0,0,0,0.08)] dark:shadow-[0_-4px_24px_rgba(0,0,0,0.3)]">
      <div className="mx-auto max-w-screen-lg px-4 py-4 sm:px-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:gap-8">

          {/* Left: copy + rows */}
          <div className="flex-1 min-w-0">
            <div className="mb-3">
              <p className="text-sm font-semibold text-foreground">Cookie preferences</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                We use cookies to keep the app running and to improve your experience. You're in control.{" "}
                <a
                  href="https://fintective.ai/privacy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="underline hover:text-foreground transition-colors"
                >
                  Privacy Policy
                </a>
              </p>
            </div>

            <div className="rounded-lg border border-border bg-muted/30 px-3 divide-y divide-border">
              <CategoryRow
                title="Necessary"
                locked
                checked
                description="Session cookies that keep you signed in and remember your display settings. These are essential for the app to function and cannot be turned off."
              />
              <CategoryRow
                title="Analytics (Optional)"
                checked={analytics}
                onCheckedChange={setAnalytics}
                description="Usage data helps us understand how the app is used so we can improve it. No financial data (transactions, amounts or budgets) is ever included."
              />
            </div>
          </div>

          {/* Right: actions */}
          <div className="flex shrink-0 items-end gap-2 sm:flex-col sm:justify-end sm:items-stretch">
            <Button size="sm" onClick={handleAcceptAll} className="flex-1 sm:flex-none">
              Accept all
            </Button>
            <Button size="sm" variant="outline" onClick={handleSave} className="flex-1 sm:flex-none">
              Save preferences
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
